import { AfterViewInit, Component, ContentChildren, ElementRef, EventEmitter, Input, Output, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { MenuItem, TooltipOptions } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { NxTemplateDirective } from '../../directives/nx-template.directive';

export interface CommandMenuItem {
  label: string;
  command: string;
  disabled?: () => boolean;
  data?: any;
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: "nx-default-button",
  templateUrl: "default-button.html",
  host: {
    'class': 'nx-default-button'
  },
  styleUrls: ["./default-button.scss"]
})
export class DefaultButtonComponent implements AfterViewInit {

  @ViewChild('menu') menu: Menu;

  /**
   * The template for each menu item
   */
  public menuItemTemplate: TemplateRef<any> | undefined;

  public menuEndTemplate: TemplateRef<any> | undefined;

  /**
   * All templates that are inside the box tag in the parent component
   */
  @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;

  tooltipOptions: TooltipOptions = {
    hideDelay: 150,
    showDelay: 300,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom'
  };

  @Input() label: string;
  @Input() icon: string;
  @Input() disabled: boolean = false;
  @Input() tooltip: string;
  @Input() tooltipPosition = "bottom";
  @Input() clickable: boolean = true;
  @Input() menuItems: MenuItem[] | undefined;
  @Input() menuWithIcon: boolean = false;
  @Input() menuEndContent: string;
  @Input() commandMenuItems: CommandMenuItem[] | undefined;
  @Input() style: "light" | "transparent" | "dark" | "accent" = "transparent";
  @Input() width: 'auto' | 'full' = 'auto';
  @Input() iconPos: "left" | "right" = "left";
  @Input() badgeValue: number | undefined;
  @Input() data: any;
  @Output() onCommand: EventEmitter<any> = new EventEmitter();
  @Output() onClick: EventEmitter<MouseEvent> = new EventEmitter();

  constructor(
    private elRef: ElementRef
  ) {
  }

  getNativeElement(): HTMLElement {
    return this.elRef.nativeElement;
  }

  ngAfterViewInit() {
    // Find the ul element
    const ul = this.elRef.nativeElement.querySelector('ul.p-menu-list');
    
    if (ul) {
      // Create new element
      const newDiv = document.createElement('div');
      newDiv.innerHTML = 'Your content here';
      newDiv.className = 'your-classes-here';
      
      // Insert after ul
      ul.insertAdjacentElement('afterend', newDiv);
    }
  }

    /**
   * Walks through all templates and assigns them to the corresponding variables
   */
  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'menuItem':
          this.menuItemTemplate = item.template;
        break;
        case 'menuEndTemplate':
          this.menuEndTemplate = item.template;
        break;
      }
    });
  }

  getButtonClasses(): string {
    let classes = "";
    if(this.label !== undefined && this.icon === undefined) {
      classes = "p-button-text-only";
    }
    if(this.label !== undefined && this.icon !== undefined) {
      classes = "p-button-text-and-icon";
    }
    if(this.width === 'full') {
      classes += " p-button-full-width";
    }
    // icon only will be added from prime
    return classes;
  }

  hasMenu(): boolean {
    return this.menuItems !== undefined && this.menuItems.length > 0;
  }

  getIconPosClass(): string {
    if(this.icon === undefined) {
      return undefined;
    }
    return this.iconPos;
  }

  getAdditionalClasses(): string {
    let additionalClasses = this.style;
    if(this.getIconPosClass() !== undefined) {
      additionalClasses += " icon-" + this.getIconPosClass();
    }
    if(!this.clickable) {
      additionalClasses += " not-clickable";
    }
    return additionalClasses;
  }

  getBadgeValue(): string | undefined {
    if(this.badgeValue === undefined) {
      return undefined;
    }
    if(this.badgeValue > 9) {
      return '9+';
    }
    if(this.badgeValue < 1) {
      return undefined;
    }
    return this.badgeValue + '';
  }

  click($event) {
    // we have a "standard menu" defined in the parent component
    if(this.hasMenu()) {
      $event.stopPropagation();
      this.menu.toggle($event);
      return;
    }
    // we have a menu that works with stateless commands to avoid menu recreations
    // during change detection
    if(this.commandMenuItems !== undefined && this.commandMenuItems.length > 0) {
      this.menuItems = new Array<MenuItem>();
      for (const item of this.commandMenuItems) {
        this.menuItems.push({
          label: item.label,
          disabled: item.disabled?.call(this),
          command: () => {
            this.onCommand.emit({
              command: item.command,
              data: this.data
            });
          }
        });
      }
      this.menu.toggle($event);
      return;
    }
    // its a default button
    this.onClick.emit($event);
  }
}
